import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Copy, Check, Users, X } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import { invitationService } from '../../services/invitationService';
import { sendFamilyInvitationEmail } from '../../services/invitationEmailService';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Modal } from '../ui/modal';

const inviteSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

const InvitePartnerModal = ({ isOpen, onClose }) => {
  const { user, userProfile } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [invitationSent, setInvitationSent] = useState(false);
  const [invitationLink, setInvitationLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    reset,
  } = useForm({
    resolver: zodResolver(inviteSchema),
  });

  const onSubmit = async (data) => {
    if (!userProfile?.familyId) {
      setError('No family found. Please try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create invitation in database
      const result = await invitationService.sendInvitation(
        userProfile.familyId,
        userProfile.uid,
        data.email
      );

      if (result.success) {
        const link = invitationService.createInvitationLink(result.token);
        setInvitationLink(link);
        
        // Send email invitation
        const emailResult = await sendFamilyInvitationEmail({
          invitedEmail: data.email,
          inviterName: userProfile.displayName || user?.email || 'Your family member',
          inviterEmail: user?.email || '',
          familyName: 'Family Finance',
          invitationLink: link,
          expiresAt: '7 days'
        });

        if (emailResult.success) {
          console.log('✅ Email invitation sent successfully');
        } else {
          console.warn('⚠️ Email invitation failed, but invitation link was created:', emailResult.error);
        }
        
        setInvitationSent(true);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Failed to send invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(invitationLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const handleClose = () => {
    // Reset form state when closing
    setInvitationSent(false);
    setInvitationLink('');
    setError('');
    setCopied(false);
    reset();
    onClose();
  };

  const handleSendAnother = () => {
    setInvitationSent(false);
    setInvitationLink('');
    setError('');
    setCopied(false);
    reset();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={invitationSent ? "Invitation Sent!" : "Invite Your Partner"}
      description={invitationSent ? "Your partner can now join your family account" : "Share your family finances together"}
      size="default"
    >
      {invitationSent ? (
        <div className="space-y-6">
          {/* Success Icon */}
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Invitation Sent Successfully!
            </h3>
            <p className="text-sm text-gray-600">
              We've sent an invitation to <span className="font-medium">{getValues('email')}</span>
            </p>
          </div>

          {/* Invitation Link */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Invitation Link
            </label>
            <div className="flex space-x-2">
              <Input
                value={invitationLink}
                readOnly
                className="flex-1 text-sm"
              />
              <Button
                onClick={copyToClipboard}
                variant="outline"
                size="sm"
                className="flex-shrink-0"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              This link will expire in 7 days
            </p>
          </div>

          {/* Next Steps */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-sm text-blue-800 font-medium mb-2">
              What happens next?
            </p>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Your partner will receive an email with the invitation</li>
              <li>• They can click the link to create an account or sign in</li>
              <li>• Once accepted, you'll both have access to shared finances</li>
              <li>• All expenses and budgets will be synced between you</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <Button
              onClick={handleClose}
              className="flex-1"
            >
              Done
            </Button>
            <Button
              variant="outline"
              onClick={handleSendAnother}
              className="flex-1"
            >
              Send Another
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {/* Email Input */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Partner's Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="email"
                type="email"
                placeholder="partner@example.com"
                className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                {...register('email')}
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>

          {/* Info Section */}
          <div className="bg-gray-50 rounded-md p-4">
            <div className="flex items-start space-x-3">
              <Users className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-700 font-medium mb-1">
                  How it works
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Your partner will receive an email invitation</li>
                  <li>• They can create an account or sign in with existing credentials</li>
                  <li>• Once accepted, you'll share the same family account</li>
                  <li>• All expenses and budgets will be synced between you</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1"
            >
              {loading ? 'Sending...' : 'Send Invitation'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default InvitePartnerModal;
